/**
 * QA Detail Modal
 * Shows full question and answer text when a row is clicked
 * Supports editing with dropdown selections for program codes, statuses, and visa requirements
 */

import React, { useEffect, useState } from 'react';
import type { QAQuestion } from '../../types/qa-management';
import {
  capitalizeSchool,
  getProgramSchoolClass,
  getVisaTagClass,
  getStatusTagClass,
} from '../../utils/qa-helpers';
import { advisorApiService } from '../../services/advisor-api';
import { useAuth } from '../../contexts/AuthContext';
import { QAIncludeToggle } from './QAIncludeToggle';
import { getApiErrorMessage } from '../../utils/api-error';
import '../../styles/QADetailModal.css';

interface QADetailModalProps {
  qaData: QAQuestion | null;
  onClose: () => void;
  /** Called after a successful change. Receives the updated row for a toggle; nothing for edit/delete. */
  onUpdate?: (updated?: QAQuestion) => void;
}

export const QADetailModal: React.FC<QADetailModalProps> = ({ qaData, onClose, onUpdate }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Inclusion toggle (independent of edit mode)
  const [isIncluded, setIsIncluded] = useState(true);
  const [isTogglingIncluded, setIsTogglingIncluded] = useState(false);

  // Editable fields
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [programCodes, setProgramCodes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [visaRequirements, setVisaRequirements] = useState<string[]>([]);

  // Available options from backend
  const [availableProgramCodes, setAvailableProgramCodes] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availableVisaRequirements, setAvailableVisaRequirements] = useState<string[]>([]);

  // Check if user can edit (user or admin role)
  const canEdit = user?.role === 'user' || user?.role === 'admin';

  // Load initial data when modal opens
  useEffect(() => {
    if (qaData) {
      setQuestion(qaData.question);
      setAnswer(qaData.answer);
      setProgramCodes(qaData.programCodes);
      setStatuses(qaData.statuses);
      setVisaRequirements(qaData.visaRequirements);
      setIsIncluded(qaData.isIncluded);
      setIsEditing(false);
      setError(null);
      setSuccess(false);
    }
  }, [qaData]);

  // Fetch available options when entering edit mode
  useEffect(() => {
    if (isEditing && canEdit) {
      fetchAvailableOptions();
    }
  }, [isEditing, canEdit]);

  const fetchAvailableOptions = async () => {
    try {
      const [programCodesRes, statusesRes, visaReqRes] = await Promise.all([
        advisorApiService.getProgramCodes(),
        advisorApiService.getStatuses(),
        advisorApiService.getVisaRequirements(),
      ]);

      if (programCodesRes.data) {
        setAvailableProgramCodes(programCodesRes.data.program_codes);
      }
      if (statusesRes.data) {
        setAvailableStatuses(statusesRes.data.statuses);
      }
      if (visaReqRes.data) {
        setAvailableVisaRequirements(visaReqRes.data.visa_requirements);
      }
    } catch (err) {
      console.error('Failed to fetch available options:', err);
      setError('Failed to load dropdown options');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    if (qaData) {
      setQuestion(qaData.question);
      setAnswer(qaData.answer);
      setProgramCodes(qaData.programCodes);
      setStatuses(qaData.statuses);
      setVisaRequirements(qaData.visaRequirements);
    }
    setIsEditing(false);
    setError(null);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!qaData) return;

    // Validate fields
    if (!question.trim()) {
      setError('Question is required');
      return;
    }
    if (!answer.trim()) {
      setError('Answer is required');
      return;
    }
    if (programCodes.length === 0) {
      setError('At least one program code is required');
      return;
    }
    if (statuses.length === 0) {
      setError('At least one status is required');
      return;
    }
    if (visaRequirements.length === 0) {
      setError('At least one visa requirement is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await advisorApiService.updateQAQuestion(qaData.questionId, {
        question: question.trim(),
        answer: answer.trim(),
        program_codes: programCodes,
        statuses: statuses,
        visa_requirements: visaRequirements,
      });

      if (response.data) {
        setSuccess(true);
        setIsEditing(false);

        // Fetch the updated question to refresh the modal
        const updatedResponse = await advisorApiService.getQAQuestion(qaData.questionId);
        if (updatedResponse.data) {
          // Update local state with fresh data
          setQuestion(updatedResponse.data.question);
          setAnswer(updatedResponse.data.answer);
          setProgramCodes(updatedResponse.data.programCodes);
          setStatuses(updatedResponse.data.statuses);
          setVisaRequirements(updatedResponse.data.visaRequirements);
        }

        // Call the parent callback to refresh the list
        if (onUpdate) {
          onUpdate();
        }

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(response.error || 'Failed to update question');
      }
    } catch (err) {
      console.error('Failed to update question:', err);

      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      setError(
        status === 409
          ? 'A question with the same question and answer already exists in the database'
          : getApiErrorMessage(err, 'Failed to update question')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const toggleArrayItem = (arr: string[], item: string, setter: (arr: string[]) => void) => {
    if (arr.includes(item)) {
      setter(arr.filter((i) => i !== item));
    } else {
      setter([...arr, item]);
    }
  };

  const handleToggleIncluded = async (nextIncluded: boolean) => {
    if (!qaData || nextIncluded === isIncluded || isTogglingIncluded) return;

    setIsTogglingIncluded(true);
    setError(null);

    try {
      const response = await advisorApiService.setQAIncluded(qaData.questionId, nextIncluded);

      if (response.data) {
        setIsIncluded(response.data.isIncluded);
        if (onUpdate) {
          onUpdate(response.data);
        }
      } else {
        setError(response.error || 'Failed to update inclusion');
      }
    } catch (err) {
      console.error('Failed to toggle inclusion:', err);
      setError(getApiErrorMessage(err, 'Failed to update inclusion'));
    } finally {
      setIsTogglingIncluded(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setError(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleDeleteConfirm = async () => {
    if (!qaData) return;

    setIsDeleting(true);
    setError(null);

    try {
      const response = await advisorApiService.deleteQAQuestion(qaData.questionId);

      if (response.data) {
        // Call the parent callback to refresh the list
        if (onUpdate) {
          onUpdate();
        }

        // Close the modal immediately after successful deletion
        onClose();
      } else {
        setError(response.error || 'Failed to delete question');
        setShowDeleteConfirm(false);
      }
    } catch (err) {
      console.error('Failed to delete question:', err);

      setError(getApiErrorMessage(err, 'Failed to delete question'));
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!qaData) return null;

  // Check if Save button should be disabled
  const isSaveDisabled =
    isSaving ||
    !question.trim() ||
    !answer.trim() ||
    programCodes.length === 0 ||
    statuses.length === 0 ||
    visaRequirements.length === 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="qa-modal-overlay" onClick={onClose}>
      <div className="qa-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="qa-modal-header">
          <h2>Question & Answer Details</h2>
          <button
            type="button"
            className="qa-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="qa-modal-body">
          {/* Success Message */}
          {success && <div className="qa-modal-success">Question updated successfully!</div>}

          {/* Error Message */}
          {error && <div className="qa-modal-error">{error}</div>}

          {/* Question Section */}
          <div className="qa-modal-section">
            <h3>Question</h3>
            {isEditing ? (
              <textarea
                className="qa-modal-textarea"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                placeholder="Enter question..."
              />
            ) : (
              <div className="qa-modal-text">{question}</div>
            )}
          </div>

          {/* Answer Section */}
          <div className="qa-modal-section">
            <h3>Answer</h3>
            {isEditing ? (
              <textarea
                className="qa-modal-textarea"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={6}
                placeholder="Enter answer..."
              />
            ) : (
              <div className="qa-modal-text qa-modal-answer">{answer}</div>
            )}
          </div>

          {/* Program Codes Section */}
          <div className="qa-modal-section">
            <h3>Program Codes</h3>
            {isEditing ? (
              <div className="qa-modal-checkbox-group">
                {availableProgramCodes.map((code) => (
                  <label key={code} className="qa-modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={programCodes.includes(code)}
                      onChange={() => toggleArrayItem(programCodes, code, setProgramCodes)}
                    />
                    <span>{code}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="qa-modal-pills">
                {programCodes.map((code, idx) => {
                  const info = qaData.programInfo.find((p) => p.programCode === code);
                  const tooltipText =
                    info?.studentType && info?.school && info?.program
                      ? `${info.studentType} | ${capitalizeSchool(info.school)} | ${info.program}`
                      : '';

                  return (
                    <span
                      key={`modal-prog-${idx}`}
                      className={`qa-pill program-pill ${code === 'Apply to all' ? 'school-apply-to-all' : getProgramSchoolClass(info?.school)}`}
                      title={tooltipText}
                    >
                      {code.toUpperCase()}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Visa Requirements Section */}
          <div className="qa-modal-section">
            <h3>Visa Requirements</h3>
            {isEditing ? (
              <div className="qa-modal-checkbox-group">
                {availableVisaRequirements.map((visa) => (
                  <label key={visa} className="qa-modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={visaRequirements.includes(visa)}
                      onChange={() => toggleArrayItem(visaRequirements, visa, setVisaRequirements)}
                    />
                    <span>{visa}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="qa-modal-pills">
                {visaRequirements.map((visa, idx) => (
                  <span
                    key={`modal-visa-${idx}`}
                    className={`qa-pill visa-pill ${getVisaTagClass(visa)}`}
                  >
                    {visa}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Status Section */}
          <div className="qa-modal-section">
            <h3>Status</h3>
            {isEditing ? (
              <div className="qa-modal-checkbox-group">
                {availableStatuses.map((status) => (
                  <label key={status} className="qa-modal-checkbox-label">
                    <input
                      type="checkbox"
                      checked={statuses.includes(status)}
                      onChange={() => toggleArrayItem(statuses, status, setStatuses)}
                    />
                    <span>{status}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="qa-modal-pills">
                {statuses.map((status, idx) => (
                  <span
                    key={`modal-status-${idx}`}
                    className={`qa-pill status-pill ${getStatusTagClass(status)}`}
                  >
                    {status}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Inclusion Section */}
          <div className="qa-modal-section">
            <h3>Include in Chatbot</h3>
            <div className="qa-modal-include-row">
              <QAIncludeToggle
                value={isIncluded}
                onChange={handleToggleIncluded}
                disabled={!canEdit || isTogglingIncluded || isEditing}
                label="Include in chatbot"
              />
              <div className="qa-modal-include-hint">
                {isIncluded
                  ? 'The chatbot can use this Q&A to answer students.'
                  : 'Excluded: kept in the database but never retrieved by the chatbot. Set to Yes to add it back.'}
              </div>
            </div>
          </div>

          {/* Metadata Section */}
          <div className="qa-modal-meta">
            <div className="qa-modal-meta-item">
              <strong>Created:</strong> {formatDate(qaData.createdAt)}
            </div>
            <div className="qa-modal-meta-item">
              <strong>Last Updated:</strong> {formatDate(qaData.updatedAt)}
            </div>
            <div className="qa-modal-meta-item">
              <strong>Question ID:</strong> <code>{qaData.questionId}</code>
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="qa-modal-footer">
          {isEditing ? (
            <>
              <button
                type="button"
                className="qa-modal-button qa-modal-button-secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="qa-modal-button qa-modal-button-primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <div className="qa-modal-edit-button-wrapper">
                <button
                  type="button"
                  className="qa-modal-button qa-modal-button-danger"
                  onClick={handleDeleteClick}
                  disabled={!canEdit}
                  title={
                    !canEdit
                      ? `You need 'user' or 'admin' permission to delete. Current role: ${user?.role || 'none'}`
                      : 'Delete this question from the database. Only roles user and admin can perform this operation.'
                  }
                >
                  Delete
                </button>
              </div>
              <div className="qa-modal-edit-button-wrapper">
                <button
                  type="button"
                  className="qa-modal-button qa-modal-button-primary"
                  onClick={handleEdit}
                  disabled={!canEdit}
                  title={
                    !canEdit
                      ? `You need 'user' or 'admin' permission to edit. Current role: ${user?.role || 'none'}`
                      : ''
                  }
                >
                  Edit
                </button>
              </div>
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="qa-delete-confirm-overlay" onClick={handleDeleteCancel}>
            <div className="qa-delete-confirm-content" onClick={(e) => e.stopPropagation()}>
              <div className="qa-delete-confirm-header">
                <h3>Confirm Delete</h3>
              </div>
              <div className="qa-delete-confirm-body">
                <p>
                  You will delete this question from the database. This operation is not revertible.
                </p>
              </div>
              <div className="qa-delete-confirm-footer">
                <button
                  type="button"
                  className="qa-modal-button qa-modal-button-secondary"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="qa-modal-button qa-modal-button-danger"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
